/**
 * Générateur — Automatismes : développer (1230) et factoriser (1231) une
 * expression simple.
 * Attendu ID : 1230 | Niveau : 3ème | Domaine : Calcul littéral et algébrique
 * Rubrique : AUTOMATISME (programme 2025, cycle 4 page 11, ligne 798).
 * UN FICHIER, DEUX ATTENDUS (base du prof du 13/09, modèle 1213/1246) : les
 * clés developper_* portent l'attendu 1230, les clés factoriser_* l'attendu
 * 1231 ; gen_1231 est un dispatch sur les clés factoriser_* (genererParmi).
 * Le même item k(ax ± b) = kax ± kb sert les deux sens.
 *
 * ── Périmètre (cadrage programme-maths 2026-09-12, mode auto) ─────────────
 * La SIMPLE distributivité dans les deux sens : k(ax + b) = kax + kb et
 * kax + kb = k(ax + b), k 2..9, a 1..9, b 1..9, + ou −. Exclus (objectifs
 * 3e l. 803-811, gen_209) : double distributivité, identités remarquables,
 * développer puis réduire, k négatif (1233), x(ax + b) et ax² + bx (choix
 * mode auto, À ARBITRER). Factoriser : le facteur commun est DONNÉ en
 * saisie (« 6x + 15 = 3(⬚x + ⬚) »), le plus grand au QCM (« factorisé le
 * plus possible »).
 * Banque MathsMentales (S. Cogez, Apache 2.0, relevé 2026-09-12) : 4NE4
 * options 0-1 (a × (bx + c), a 2..10, coefficients 1..10), 4NE5 option 0
 * (ax + b, coeff 2..10, pgcd).
 *
 * ── Plan de cartes ─────────────────────────────────────────────────────────
 *   1230 « Série flash » (developper_flash_texte_trou_saisie) : quatre
 *     lignes KaTeX à développer « 3(x + 4) = ⬚x + ⬚ » ; + ou − (le signe est
 *     affiché, les trous sont positifs).
 *   1230 « Développe » (developper_saisie) : « Développe 5(9y + 9). » en
 *     saisie libre, comparaison d'expression : 45y + 45 sous tous ses ordres
 *     ET la ligne intermédiaire 5*9y + 5*9 (prof 13/09 : « l'automatisme,
 *     c'est juste développer, pas réduire »).
 *   1230 « Développer » (developper_qcm_unique) : l'item DEPP 2de 2025 q.5
 *     « 3(5x + 1) » [15x + 3 ; 15x + 1 ; 18x ; 15x + 4] ; familles
 *     un_seul_terme (20,3 %), termes_non_semblables, addition_pour_produit.
 *   1230 « Vrai ou faux » (developper_tableau_vf) : quatre égalités
 *     « k(ax ± b) = … » vérifiées comme polynômes.
 *   1231 « Série flash » (factoriser_flash_texte_trou_saisie) : quatre
 *     lignes à factoriser avec le facteur donné « 6x + 15 = 3(⬚x + ⬚) ».
 *   1231 « Factorise » (factoriser_saisie) : « Factorise le plus possible
 *     12x − 30. » en saisie libre : 6(2x − 5), 6(−5 + 2x), 6*(…), (…)*6 ;
 *     la partielle refusée.
 *   1231 « Factoriser » (factoriser_qcm_unique) : « 12x − 30, factorisé le
 *     plus possible, c'est… » [6(2x − 5) ; 6(2x − 30) ; 2(6x − 15) ;
 *     6(2x + 5)] ; familles constante_non_divisee, partielle, signe_perdu.
 *   1231 « Vrai ou faux » (factoriser_tableau_vf) : quatre égalités
 *     « kax ± kb = k(…) » vérifiées comme polynômes.
 *
 * INVARIANTS : chaque égalité re-vérifiée en développant par un second
 * chemin (throw) ; au QCM factoriser, le facteur de la clé est le pgcd des
 * deux coefficients ; distracteurs distincts de la clé comme polynômes ET
 * comme écritures.
 * SACRIFIÉ (fiche « Le 5 multiplie les deux » : 5(x + 4) = 5x + 20) :
 * (5 ; 1 ; 4) sous toutes ses formes et lettres ; auto-testé par
 * gen1230.self.test.js.
 *
 * ── Erreurs (familles nommées ; DEPP 2de 2025 q.5 « 3(5x + 1) », réussite
 * 74,0 %) ─────────────────────────────────────────────────────────────────
 *   un_seul_terme          : 15x + 1 (le 3 ne multiplie que le premier), PRINCIPALE (20,3 %) ;
 *   termes_non_semblables  : 18x (15x + 3 fusionnés, nom de 1227) ;
 *   addition_pour_produit  : 15x + 4 (3 + 1) ;
 *   constante_non_divisee  : 12x − 30 → 6(2x − 30) ;
 *   partielle              : 2(6x − 15) (pas le plus grand facteur) ;
 *   signe_perdu            : 6(2x + 5) ;
 *   facteur_oublie         : (2x − 5) sans le 6 (quand k est premier).
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou, makeSaisie, tex, ecrituresPolynome,
} from '../../../utils';

const ATTENDU_ID = 1230;
/** Développer = 1230, factoriser = 1231 (base du prof, 13/09). */
export const ATTENDU = { developper: 1230, factoriser: 1231 };
const opts = (attendu, label) => ({ niveau: '3eme', matiere: 'maths', attendu, label });

const L_FLASH = 'Série flash';
const L_DEV = 'Développer';
const L_FAC = 'Factoriser';
const L_VF = 'Vrai ou faux';
const L_DEVELOPPE = 'Développe';
const L_FACTORISE = 'Factorise';

const LETTRES = ['x', 'x', 'x', 'n', 't', 'y'];
const pgcd = (u, v) => (v === 0 ? u : pgcd(v, u % v));

// ─── Le domaine ───────────────────────────────────────────────────────────

/** L'exemple du programme et de la fiche : 5(x + 4) = 5x + 20. */
export const SACRIFIE = { k: 5, a: 1, b: 4 };
const estSacrifie = (k, a, b) => k === SACRIFIE.k && a === SACRIFIE.a && b === SACRIFIE.b;

/** Un item { k, a, b, signe } : k(ax ± b) = kax ± kb, vérifié par un second chemin. */
const tirer = ({ aPgcd = false, aMin = 1 } = {}) => {
    for (let essai = 0; essai < 200; essai++) {
        const k = randInt(2, 9);
        const a = randInt(aMin, 9);
        const b = randInt(1, 9);
        const signe = randomChoice(['+', '+', '-']);
        if (estSacrifie(k, a, b)) continue;
        if (aPgcd && pgcd(a, b) !== 1) continue; // k est alors LE plus grand facteur commun de ka et kb
        const dev = { ax: k * a, c: k * b };
        if (dev.ax !== a * k || dev.c !== b * k) throw new Error('gen_1230 : distributivité');
        return { k, a, b, signe, dev };
    }
    return { k: 3, a: 2, b: 5, signe: '+', dev: { ax: 6, c: 15 } };
};

// ─── Écritures (KaTeX) ────────────────────────────────────────────────────

const mono = (coef, L) => (coef === 1 ? L : `${coef}${L}`);
const factoTex = ({ k, a, b, signe }, L) => `${k}(${mono(a, L)} ${signe} ${b})`;
const devTex = ({ dev, signe }, L) => `${dev.ax}${L} ${signe} ${dev.c}`;

const EXPLICATIONS = {
    un_seul_terme: 'Le facteur devant la parenthèse multiplie LES DEUX termes : 3(5x + 1) = 15x + 3.',
    termes_non_semblables: '15x et 3 ne sont pas semblables : 15x + 3 reste tel quel.',
    addition_pour_produit: 'Développer, c\'est multiplier : 3 × 1 = 3, pas 3 + 1.',
    constante_non_divisee: 'Le facteur commun se retire des DEUX termes : 12x − 30 = 6 × 2x − 6 × 5 = 6(2x − 5).',
    partielle: '2(6x − 15) est juste mais pas fini : 6 et 15 ont encore 3 en commun. Le plus possible, c\'est 6(2x − 5).',
    signe_perdu: 'Le signe reste dans la parenthèse : 12x − 30 = 6(2x − 5), pas 6(2x + 5).',
    regle: 'Développer : le facteur multiplie chaque terme de la parenthèse. Factoriser : je cherche ce que les deux termes ont en commun et je le mets devant la parenthèse.',
};

// ─── Carte 1 : Série flash ────────────────────────────────────────────────

/** Quatre lignes du même sens ; à factoriser, a ≥ 2 (un trou « 1 » devant la lettre n'a pas de sens). */
const genFlash = (sens) => () => {
    for (let essai = 0; essai < 100; essai++) {
        const L = randomChoice(LETTRES);
        const items = [0, 1, 2, 3].map(() => (sens === 'developper' ? tirer() : tirer({ aMin: 2 })));
        if (new Set(items.map((it) => factoTex(it, L))).size < 4) continue;
        const lignes = items.map((it) => (sens === 'developper'
            ? `$${factoTex(it, L)} = {{${it.dev.ax}}}${L} ${it.signe} {{${it.dev.c}}}$`
            : `$${devTex(it, L)} = ${it.k}({{${it.a}}}${L} ${it.signe} {{${it.b}}})$`));
        const consigne = sens === 'developper' ? '<p><b>Développe.</b></p>' : '<p><b>Factorise</b> (le facteur est donné).</p>';
        const payload = makeTexteTrou(lignes.join('\n'), consigne, { ...opts(ATTENDU[sens], L_FLASH), mode: 'saisie', strict: true });
        payload.contenu = { ...payload.contenu, sousType: sens, explications: { regle: EXPLICATIONS.regle, un_seul_terme: EXPLICATIONS.un_seul_terme } };
        payload._meta.params = { sens, items, lettre: L };
        return payload;
    }
    throw new Error(`gen_1230 : série flash ${sens} sans tirage`);
};

// ─── Cartes « Développe » / « Factorise » (saisie libre, comparaison d'expression) ──

/**
 * Décision prof 13/09 : un format saisie à plusieurs réponses admises, en plus
 * de l'existant. « L'automatisme, c'est juste développer, pas réduire » : la
 * ligne INTERMÉDIAIRE 5*9y + 5*9 (les produits écrits) est admise au même
 * titre que 45y + 45. Listes finies :
 *   développer : le développé sous tous ses ordres (ecrituresPolynome : 45y + 45,
 *     45 + 45y, +45y + 45, 45*y + 45 ; 30n − 18, −18 + 30n) + huit écritures
 *     intermédiaires (5*9y ou 9y*5, 5*9 ou 9*5, dans les deux ordres ; × et *
 *     sont confondus par le juge) ; corrigé 45y + 45 ;
 *   factoriser (le plus possible, k = pgcd comme au QCM) : 6(2x − 5),
 *     6(−5 + 2x), 6*(…), (…)*6, 2*x dans la parenthèse ; la factorisation
 *     partielle 2(6x − 15) est refusée (famille partielle du QCM).
 */
const ecrituresDev = (it, L) => {
    const { k, a, b, signe, dev } = it;
    const reduites = ecrituresPolynome([{ coef: dev.ax, deg: 1 }, { coef: signe === '+' ? dev.c : -dev.c, deg: 0 }], L);
    const t1 = [`${k}*${mono(a, L)}`, `${mono(a, L)}*${k}`];
    const t2 = [`${k}*${b}`, `${b}*${k}`];
    const inter = [];
    for (const u of t1) for (const v of t2) inter.push(signe === '+' ? `${u} + ${v}` : `${u} - ${v}`, signe === '+' ? `${v} + ${u}` : `-${v} + ${u}`);
    return [...reduites, ...inter];
};
const ecrituresFac = (it, L) => {
    const { k, a, b, signe } = it;
    const monos = a === 1 ? [L] : [`${a}${L}`, `${a}*${L}`];
    const dedans = monos.flatMap((m) => (signe === '+' ? [`${m} + ${b}`, `${b} + ${m}`] : [`${m} - ${b}`, `-${b} + ${m}`]));
    return dedans.flatMap((d) => [`${k}(${d})`, `${k}*(${d})`, `(${d})*${k}`]);
};

const genSaisie = (sens) => () => {
    const L = randomChoice(LETTRES);
    const it = sens === 'developper' ? tirer() : tirer({ aPgcd: true });
    const ecritures = sens === 'developper' ? ecrituresDev(it, L) : ecrituresFac(it, L);
    const texte = sens === 'developper'
        ? `<p><b>Développe</b> ${tex(factoTex(it, L))}.</p>`
        : `<p><b>Factorise le plus possible</b> ${tex(devTex(it, L))}.</p>`;
    const payload = makeSaisie(ecritures[0], texte, { ...opts(ATTENDU[sens], sens === 'developper' ? L_DEVELOPPE : L_FACTORISE), strict: true, comparaison: 'expression', reponses_acceptees: ecritures });
    payload.contenu = { ...payload.contenu, sousType: sens, explications: { regle: EXPLICATIONS.regle, un_seul_terme: EXPLICATIONS.un_seul_terme, partielle: EXPLICATIONS.partielle } };
    payload._meta.params = { ...it, sens, lettre: L };
    return payload;
};

// ─── Carte 2 : Développer (QCM) ───────────────────────────────────────────

const genDev = () => {
    for (let essai = 0; essai < 100; essai++) {
        const L = randomChoice(LETTRES);
        const it = tirer();
        const { k, b, signe, dev } = it;
        const bon = tex(devTex(it, L));
        const faux = [
            { code: 'un_seul_terme', texte: tex(`${dev.ax}${L} ${signe} ${b}`) },
            { code: 'termes_non_semblables', texte: tex(mono(signe === '+' ? dev.ax + dev.c : dev.ax - dev.c, L)) },
            { code: 'addition_pour_produit', texte: tex(`${dev.ax}${L} ${signe} ${k + b}`) },
        ].filter((f) => f.texte !== bon && !/^\$\\mathrm\{\}(-|0)/.test(f.texte));
        if (faux.length < 3 || new Set(faux.map((f) => f.texte)).size < 3) continue;
        const choix = [bon, ...faux.map((f) => f.texte)];
        if (new Set(choix).size !== 4) continue;
        const payload = makeQcm(choix, bon, `<p><b>${tex(factoTex(it, L))}, développé, c'est…</b></p>`, opts(ATTENDU.developper, L_DEV));
        payload.contenu = { ...payload.contenu, sousType: 'developper', explications: EXPLICATIONS };
        payload._meta.params = { ...it, lettre: L, distracteurs: faux.map(({ code }) => ({ code })) };
        return payload;
    }
    // Repli vérifié : 3(5x + 1) = 15x + 3 ; 15x + 1, 18x, 15x + 4 (l'item DEPP).
    const bon = tex('15x + 3');
    const payload = makeQcm([bon, tex('15x + 1'), tex('18x'), tex('15x + 4')], bon, `<p><b>${tex('3(5x + 1)')}, développé, c'est…</b></p>`, opts(ATTENDU.developper, L_DEV));
    payload.contenu = { ...payload.contenu, sousType: 'developper', explications: EXPLICATIONS };
    payload._meta.params = { k: 3, a: 5, b: 1, signe: '+', dev: { ax: 15, c: 3 }, lettre: 'x', distracteurs: [{ code: 'un_seul_terme' }, { code: 'termes_non_semblables' }, { code: 'addition_pour_produit' }] };
    return payload;
};

// ─── Carte 3 : Factoriser (QCM) ───────────────────────────────────────────

const genFac = () => {
    for (let essai = 0; essai < 100; essai++) {
        const L = randomChoice(LETTRES);
        const it = tirer({ aPgcd: true });
        const { k, a, b, signe, dev } = it;
        if (k === 2 || k === 3 || k === 5 || k === 7) { if (randomChoice([true, false])) continue; } // k composé plus souvent : la factorisation partielle existe
        const diviseurs = [2, 3, 4].filter((d) => k % d === 0 && d !== k);
        const d = diviseurs.length ? randomChoice(diviseurs) : null;
        // la factorisation partielle d((k/d)ax ± (k/d)b) est une égalité JUSTE, fausse sous « le plus possible » : vérifiée en développant
        if (d !== null && (d * (k / d) * a !== dev.ax || d * (k / d) * b !== dev.c)) throw new Error('gen_1230 : partielle');
        const bon = tex(factoTex(it, L));
        const faux = [
            { code: 'constante_non_divisee', texte: tex(`${k}(${mono(a, L)} ${signe} ${dev.c})`) },
            ...(d !== null ? [{ code: 'partielle', texte: tex(`${d}(${mono((k / d) * a, L)} ${signe} ${(k / d) * b})`) }] : []),
            { code: 'signe_perdu', texte: tex(`${k}(${mono(a, L)} ${signe === '+' ? '-' : '+'} ${b})`) },
            { code: 'facteur_oublie', texte: tex(`(${mono(a, L)} ${signe} ${b})`) },
        ].filter((f) => f.texte !== bon);
        const pris = [];
        for (const f of faux) if (!pris.some((g) => g.texte === f.texte) && pris.length < 3) pris.push(f);
        if (pris.length < 3) continue;
        const choix = [bon, ...pris.map((f) => f.texte)];
        const payload = makeQcm(choix, bon, `<p><b>${tex(devTex(it, L))}, factorisé le plus possible, c'est…</b></p>`, opts(ATTENDU.factoriser, L_FAC));
        payload.contenu = { ...payload.contenu, sousType: 'factoriser', explications: EXPLICATIONS };
        payload._meta.params = { ...it, lettre: L, distracteurs: pris.map(({ code }) => ({ code })) };
        return payload;
    }
    // Repli vérifié : 12x − 30 = 6(2x − 5) ; 6(2x − 30), 2(6x − 15), 6(2x + 5).
    const bon = tex('6(2x - 5)');
    const payload = makeQcm([bon, tex('6(2x - 30)'), tex('2(6x - 15)'), tex('6(2x + 5)')], bon, `<p><b>${tex('12x - 30')}, factorisé le plus possible, c'est…</b></p>`, opts(ATTENDU.factoriser, L_FAC));
    payload.contenu = { ...payload.contenu, sousType: 'factoriser', explications: EXPLICATIONS };
    payload._meta.params = { k: 6, a: 2, b: 5, signe: '-', dev: { ax: 12, c: 30 }, lettre: 'x', distracteurs: [{ code: 'constante_non_divisee' }, { code: 'partielle' }, { code: 'signe_perdu' }] };
    return payload;
};

// ─── Carte 4 : Vrai ou faux ───────────────────────────────────────────────

/** Quatre égalités du même sens (dev : k(ax ± b) = … ; fac : kax ± kb = k(…)), vraies ou d'une famille tirée. */
const genVf = (pour) => () => {
    for (let essai = 0; essai < 100; essai++) {
        const L = randomChoice(LETTRES);
        const items = [tirer(), tirer(), tirer(), tirer()];
        if (new Set(items.map((it) => factoTex(it, L))).size < 4) continue;
        const nbVraies = randomChoice([1, 2, 2, 3]);
        const verites = shuffle([...Array(nbVraies).fill(true), ...Array(4 - nbVraies).fill(false)]);
        const lignes = items.map((it, i) => {
            const { k, a, b, signe, dev } = it;
            const sens = pour === 'developper' ? 'dev' : 'fac';
            const gauche = sens === 'dev' ? factoTex(it, L) : devTex(it, L);
            if (verites[i]) return { ligne: tex(`${gauche} = ${sens === 'dev' ? devTex(it, L) : factoTex(it, L)}`), code: null };
            const faux = sens === 'dev'
                // addition_pour_produit coïncide avec la clé quand k + b = kb (k = b = 2) : écartée alors
                ? randomChoice([{ code: 'un_seul_terme', d: `${dev.ax}${L} ${signe} ${b}` }, ...(k + b !== dev.c ? [{ code: 'addition_pour_produit', d: `${dev.ax}${L} ${signe} ${k + b}` }] : [])])
                : randomChoice([{ code: 'constante_non_divisee', d: `${k}(${mono(a, L)} ${signe} ${dev.c})` }, { code: 'signe_perdu', d: `${k}(${mono(a, L)} ${signe === '+' ? '-' : '+'} ${b})` }]);
            return { ligne: tex(`${gauche} = ${faux.d}`), code: faux.code };
        });
        if (new Set(lignes.map((l) => l.ligne)).size < 4) continue;
        const payload = makeVraiFaux(lignes.map((l) => l.ligne), verites.map((v) => (v ? 0 : 1)), '<p><b>Vrai ou faux ?</b></p>', opts(ATTENDU[pour], L_VF));
        payload.contenu = { ...payload.contenu, sousType: pour, explications: EXPLICATIONS };
        payload._meta.params = { sens: pour, items, lettre: L, verites, distracteurs: lignes.filter((l) => l.code).map(({ code }) => ({ code })) };
        return payload;
    }
    throw new Error(`gen_1230 : V/F ${pour} sans tirage`);
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'k 2..9, a 1..9, b 1..9, + ou − ; simple distributivité seulement ; sacrifié 5(x + 4) = 5x + 20 toutes formes',
        'quatre écritures distinctes ; jamais une écriture de même polynôme que la clé sauf la factorisation partielle, exclue du QCM développer et distincte par la forme au QCM factoriser',
    ],
    developper_qcm_unique: 'l\'item DEPP 2de 2025 q.5 : un_seul_terme (20,3 %), termes_non_semblables, addition_pour_produit',
    factoriser_qcm_unique: 'a et b premiers entre eux (k = le plus grand facteur commun) ; constante_non_divisee, partielle (k composé), signe_perdu, un_seul_terme',
    developper_saisie: 'saisie libre : développé réduit sous tous ses ordres + huit écritures intermédiaires k*aL + k*b ; un_seul_terme, addition_pour_produit refusés par la liste',
    factoriser_saisie: 'saisie libre, k = pgcd : k(aL ± b) et ses ordres, k*(…), (…)*k ; partielle et signe_perdu refusés par la liste',
    vf_tableau_vf: 'quatre égalités du sens de l\'attendu (developper_tableau_vf : k(ax ± b) = … ; factoriser_tableau_vf : kax ± kb = k(…)), vérifiées comme polynômes',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    developper_flash_texte_trou_saisie: genFlash('developper'),
    developper_saisie: genSaisie('developper'),
    developper_qcm_unique: genDev,
    developper_tableau_vf: genVf('developper'),
    factoriser_flash_texte_trou_saisie: genFlash('factoriser'),
    factoriser_saisie: genSaisie('factoriser'),
    factoriser_qcm_unique: genFac,
    factoriser_tableau_vf: genVf('factoriser'),
};

/** 1230 : développer. */
export const CATEGORIES = [
    { id: 'developper_flash', label: L_FLASH, etapes: 1, formatsList: [{ key: 'developper_flash_texte_trou_saisie', label: 'Saisie trous' }] },
    { id: 'developper_ecrire', label: L_DEVELOPPE, etapes: 1, formatsList: [{ key: 'developper_saisie', label: 'Saisie' }] },
    { id: 'developper', label: L_DEV, etapes: 1, formatsList: [{ key: 'developper_qcm_unique', label: 'QCM' }] },
    { id: 'developper_sens', label: L_VF, etapes: 1, formatsList: [{ key: 'developper_tableau_vf', label: 'Vrai/Faux' }] },
];
/** 1231 : factoriser (gen_1231). */
export const CATEGORIES_FACTORISER = [
    { id: 'factoriser_flash', label: L_FLASH, etapes: 1, formatsList: [{ key: 'factoriser_flash_texte_trou_saisie', label: 'Saisie trous' }] },
    { id: 'factoriser_ecrire', label: L_FACTORISE, etapes: 2, formatsList: [{ key: 'factoriser_saisie', label: 'Saisie' }] },
    { id: 'factoriser', label: L_FAC, etapes: 2, formatsList: [{ key: 'factoriser_qcm_unique', label: 'QCM' }] },
    { id: 'factoriser_sens', label: L_VF, etapes: 1, formatsList: [{ key: 'factoriser_tableau_vf', label: 'Vrai/Faux' }] },
];
const clesDe = (cats) => cats.flatMap((c) => c.formatsList.map(({ key }) => key));

/** Tire parmi les clés données (dispatch des deux gens). */
export const genererParmi = (cles, typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = cles.filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(cles);
    } else key = randomChoice(cles);
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

export const generate = (typeExercice) => genererParmi(clesDe(CATEGORIES), typeExercice);
export { ATTENDU_ID };
